import React from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EmployeeAvatar — shows the employee's uploaded photo, falling back to a
 * letter avatar when no photo is uploaded or the image fails to load.
 *
 * Usage:
 *   <EmployeeAvatar userId={u.id} name={u.name} photoUpdatedAt={u.photo_updated_at} size={40} />
 *
 * The `photoUpdatedAt` prop is used as a cache-buster so a freshly-uploaded
 * photo appears immediately without a hard refresh.
 *
 * When `zoomable` is true and the employee has an uploaded photo, clicking
 * the avatar opens a lightbox showing the picture at large size (up to 90%
 * of the viewport). Used by the Employees admin page so the sys_admin can
 * inspect ID photos without leaving the list.
 */
export default function EmployeeAvatar({
  userId,
  name = '',
  photoPath,             // presence of this field (or photoUpdatedAt) means "try to fetch"
  photoUpdatedAt,
  size = 36,
  className = '',
  bgClass = 'bg-[#655dc6]',
  textClass = 'text-white',
  fontClass = 'text-sm',
  zoomable = false,
}) {
  const [broken, setBroken] = React.useState(false);
  const [zoomOpen, setZoomOpen] = React.useState(false);
  React.useEffect(() => { setBroken(false); }, [userId, photoUpdatedAt]);

  const initial = (name || '').trim().charAt(0).toUpperCase() || '?';
  const showImage = !!userId && (!!photoPath || !!photoUpdatedAt) && !broken;
  const photoSrc = showImage
    ? `${API}/users/${userId}/photo${photoUpdatedAt ? `?v=${encodeURIComponent(photoUpdatedAt)}` : ''}`
    : null;
  const canZoom = zoomable && showImage;

  const box = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
  };

  // Close on Escape while the lightbox is open
  React.useEffect(() => {
    if (!zoomOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setZoomOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomOpen]);

  return (
    <>
      <div
        className={`inline-flex items-center justify-center rounded-full overflow-hidden ${bgClass} ${textClass} font-bold select-none ${canZoom ? 'cursor-zoom-in hover:ring-2 hover:ring-[#655dc6]/40 transition' : ''} ${className}`}
        style={box}
        data-testid={userId ? `emp-avatar-${userId}` : 'emp-avatar'}
        onClick={canZoom ? (e) => { e.stopPropagation(); setZoomOpen(true); } : undefined}
        role={canZoom ? 'button' : undefined}
        tabIndex={canZoom ? 0 : undefined}
        onKeyDown={canZoom ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setZoomOpen(true); } } : undefined}
        aria-label={canZoom ? `عرض صورة ${name}` : undefined}
      >
        {showImage ? (
          <img
            src={photoSrc}
            alt={name || 'avatar'}
            className="w-full h-full object-cover"
            onError={() => setBroken(true)}
            draggable={false}
          />
        ) : (
          <span className={fontClass}>{initial}</span>
        )}
      </div>

      {/* Lightbox — only mounted when zoom is triggered */}
      {zoomOpen && (
        <div
          className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-150"
          onClick={() => setZoomOpen(false)}
          data-testid={`emp-avatar-zoom-${userId}`}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute top-4 end-4 rounded-full bg-white/10 hover:bg-white/20 text-white w-10 h-10 flex items-center justify-center text-xl leading-none"
            onClick={(e) => { e.stopPropagation(); setZoomOpen(false); }}
            aria-label="إغلاق"
            data-testid={`emp-avatar-zoom-close-${userId}`}
          >
            ×
          </button>
          <img
            src={photoSrc}
            alt={name || 'photo'}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl shadow-2xl ring-1 ring-white/10"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />
          {name ? (
            <div className="absolute bottom-6 inset-x-0 text-center text-white/90 text-sm font-medium">
              {name}
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}
